import { apiClient } from './client'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  images?: string[]
  document_context?: string
  timestamp: string
}

export interface ChatSession {
  id: string
  document_id?: string
  messages: ChatMessage[]
  created_at: string
  updated_at: string
}

export interface ChatRequest {
  message: string
  document_id?: string
  document_context?: string
  include_full_document?: boolean
  images?: string[]
  chat_session_id?: string
  quick_action?: string
}

export interface ChatResponse {
  message: ChatMessage
  session_id: string
  usage?: {
    prompt_tokens?: number
    completion_tokens?: number
    total_tokens?: number
    input_tokens?: number
    output_tokens?: number
  }
  success: boolean
  error?: string
}

export interface QuickActionRequest {
  document_id: string
  chat_session_id?: string
  section_description?: string // For write-section action
}

class ChatAPI {
  // Send a chat message
  async chat(request: ChatRequest): Promise<ChatResponse> {
    return await apiClient.post('/api/chat', request)
  }

  // Create a new chat session
  async createSession(document_id?: string): Promise<{ session_id: string }> {
    return await apiClient.post('/api/chat/session', { document_id })
  }

  // Get a chat session with its messages
  async getSession(session_id: string): Promise<ChatSession> {
    return await apiClient.get(`/api/chat/session/${session_id}`)
  }

  // Get all chat sessions for a document
  async getDocumentSessions(document_id: string): Promise<ChatSession[]> {
    return await apiClient.get(`/api/chat/sessions/document/${document_id}`)
  }

  // Delete a chat session
  async deleteSession(session_id: string): Promise<{ message: string }> {
    return await apiClient.delete(`/api/chat/session/${session_id}`)
  }

  // Quick actions
  async continueWriting(request: QuickActionRequest): Promise<ChatResponse> {
    const params = new URLSearchParams()
    params.append('document_id', request.document_id)
    if (request.chat_session_id) {
      params.append('chat_session_id', request.chat_session_id)
    }

    return await apiClient.post(`/api/chat/quick-actions/continue?${params.toString()}`)
  }

  async writeSection(request: QuickActionRequest): Promise<ChatResponse> {
    const params = new URLSearchParams()
    params.append('document_id', request.document_id)
    params.append('section_description', request.section_description || '')
    if (request.chat_session_id) {
      params.append('chat_session_id', request.chat_session_id)
    }

    return await apiClient.post(`/api/chat/quick-actions/write-section?${params.toString()}`)
  }

  async analyzeDocument(request: QuickActionRequest): Promise<ChatResponse> {
    const params = new URLSearchParams()
    params.append('document_id', request.document_id)
    if (request.chat_session_id) {
      params.append('chat_session_id', request.chat_session_id)
    }

    return await apiClient.post(`/api/chat/quick-actions/analyze?${params.toString()}`)
  }

  // Utility function to convert file to base64
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Remove data URL prefix (data:image/jpeg;base64,)
          const base64 = reader.result.split(',')[1]
          resolve(base64)
        } else {
          reject(new Error('Failed to convert file to base64'))
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }
}

export const chatAPI = new ChatAPI()
export default chatAPI